import { type VNode } from 'vue';
import { SearchCategory } from '@monolith/legacy/types/api/category';
import type { URLManager } from '../services/url-manager/types';
import { ItemIconConfig } from '@client/modules/discovery/ui/filters/types';

export type ConfigDefinition = {
  template: SnippetTemplate;
  eventTile?: boolean;
  name: Context;
  layout: layoutRef[];
  filters: facetRef[];
  quickFilters: facetRef[];
  urlManager: URLManager;
  maxPages?: number;
};

export interface SearchRouteState {
  category?: SearchCategory;
  subcategory?: SearchCategory;
  q?: string;
  page?: number;
  hierarchicalMenu?: Record<string, string[]>;
  refinementList?: Record<string, string[]>;
  range?: Record<string, string>;
  toggle?: Record<string, string>;
  sortBy?: string;
}

export enum FacetsType {
  hierarchical = 'hierarchical',
  refinement = 'refinement',
  priceRange = 'priceRange',
  hierarchicalMultiSelect = 'hierarchicalMultiSelect',
  numeric = 'numeric',
  boolean = 'boolean',
  color = 'color',
  singleSelect = 'singleSelect',
}

export enum SnippetTemplate {
  PRODUCTS = 'products',
  BRANDS = 'brands',
}

export enum layoutRef {
  withTabs = 'withTabs',
  withBreadCrumb = 'withBreadCrumb',
  withTitle = 'withTitle',
  withCategoriesFilter = 'withCategoriesFilter',
  withSearchInput = 'withSearchInput',
  withBrandSuggestion = 'withBrandSuggestion',
  withSimilarProductsDrawer = 'withSimilarProductsDrawer',
  withCategoryTiles = 'withCategoryTiles',
  withFilterSidebar = 'withFilterSidebar',
  withQuickFilters = 'withQuickFilters',
  withSort = 'withSort',
}

export enum facetRef {
  brand = 'brand',
  tags = 'tags',
  location = 'location',
  locationBrand = 'locationBrand',
  made_in = 'made_in',
  margin = 'margin',
  marginBrand = 'marginBrand',
  shipping_lead_time = 'shipping_lead_time',
  shipping_lead_timeBrand = 'shipping_lead_timeBrand',
  retail_price = 'retail_price',
  category = 'category',
  discount = 'discount',
  maxDiscountRate = 'maxDiscountRate',
  aks_custom__loyalty_offers = 'aks_custom__loyalty_offers',
  aks_systems = 'aks_systems',
  product_type = 'product_type',
}

export enum Context {
  BRAND = 'brand',
  SEARCH = 'search',
  CATEGORY_PRODUCTS = 'category_products',
  CATEGORY_BRANDS = 'category_brands',
  NEW_PRODUCTS = 'new_products',
  NEW_BRANDS = 'new_brands',
  COLLECTION_PRODUCTS = 'collection_products',
  COLLECTION_BRANDS = 'collection_brands',
  WHOLESALE = 'wholesale',
}

export interface Facet {
  uid: string;
  name: string;
  placeHolder?: string;
  type: FacetsType;
  attributeType?: string;
  display: boolean;
  title: string;
  message: string;
  trackingName: string;
  value?: string;
  badgeType?: string;
  isOpen?: boolean;
  unit?: string;
  // Duplicated from AtributeDefinition. TODO: Unify the Facet and AttributeDefinition types
  choices?: Array<{
    slug: string;
    name: string;
    data: null | { color?: string };
    position: number;
    iconConfig?: ItemIconConfig;
  }>;
}

export type FacetGroup = {
  uid: string;
  name: string;
  title: string;
  message: string;
  display: boolean;
  facets: Facet[];
};

export interface Config {
  context: Context;
  template: SnippetTemplate;
  withBreadCrumb: boolean;
  withTitle: boolean;
  withSearchInput: boolean;
  withCategoriesFilter: boolean;
  withTabs: boolean;
  withBrandSuggestion: boolean;
  withSimilarProductsDrawer: boolean;
  withCategoryTiles: boolean;
  withFilterSidebar: boolean;
  withQuickFilters: boolean;
  withSort?: boolean;
  Facets?: (Facet | FacetGroup)[];
  quickFilters: Facet[];
  urlManager: URLManager;
  eventTile: boolean;
  maxPages?: number;
}

type InsertionIndex = number | ((hitCount: number, tilesPerRow: number) => number);

export type InsertionSlot<T> = {
  name: string;
  insertionRowIndex: InsertionIndex;
  insertionCellIndex: InsertionIndex;
  content: unknown | VNode<T>;
};

export type CatalogGridInsertion<T = unknown> = {
  status: 'inactive' | 'loading' | 'idle';
  slots: InsertionSlot<T>[];
};
